//src\Pages\User\MyAccount.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Row,
  Spinner,
  Table,
  Tab,
  Nav,
  Badge,
} from "react-bootstrap";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";

// img
import usersavatar1 from "assets/images/users/avatar-1.jpg";
import profilebg from "assets/images/profile-bg.jpg";

import {
  useConfirmStoreEmailVerifyMutation,
  useConfirmStoreMobileVerifyWithFirebaseMutation,
  useGetStoreMeQuery,
  useRequestStoreEmailVerifyMutation,
  useSetStorePasswordMutation,
  useChangeStorePasswordMutation,
  useStoreLogoutMutation,
} from "../../redux/services/store/storeAuthApi";
import { auth } from "../../config/firebase";
import {
  useGetStoreMyProfileQuery,
  useUpdateStoreMyProfileMutation,
  useGetStoreMyAddressesQuery,
  type StoreAddress,
} from "../../redux/services/store/storeAccountApi";
import {
  useGetStoreWishlistQuery,
  useRemoveStoreWishlistItemMutation,
} from "../../redux/services/store/storeWishlistApi";
import { useGetStoreOrdersQuery } from "../../redux/services/store/storeCheckoutApi";

type SettingsFormState = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
};

type VerifyFormState = {
  editing: boolean;
  otpRequested: boolean;
  otpCode: string;
  pendingValue: string;
  devOtp: string;
};

type AccountTabKey = "profile" | "list" | "order" | "setting";

declare global {
  interface Window {
    myAccountRecaptchaVerifier?: RecaptchaVerifier;
  }
}

const ACCOUNT_RECAPTCHA_ID = "my-account-firebase-recaptcha";

const canShowInvoice = (paymentStatus?: string | null) => {
  const normalized = String(paymentStatus || "").toLowerCase();
  return normalized === "paid" || normalized === "captured";
};

const getCustomizationSummary = (item?: {
  customization_enabled?: boolean | null;
  customization_color?: string | null;
  customization_note?: string | null;
}) => {
  const color = String(item?.customization_color || "").trim();
  const note = String(item?.customization_note || "").trim();
  const enabled = Boolean(item?.customization_enabled || color || note);
  if (!enabled) return "";

  const parts = [];
  if (color) parts.push(`Custom color: ${color}`);
  if (note) parts.push(`Custom note: ${note}`);
  return parts.length ? parts.join(" | ") : "Customization requested";
};

const accountSkeletonBaseStyle: React.CSSProperties = {
  background: "linear-gradient(90deg, #f1e7df 25%, #fbf7f3 50%, #f1e7df 75%)",
  backgroundSize: "200% 100%",
  animation: "accountSkeletonShimmer 1.4s ease infinite",
  borderRadius: 16,
};

const MyAccountSkeleton = () => (
  <>
    <style>
      {`
        @keyframes accountSkeletonShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}
    </style>
    <section className="position-relative">
      <div
        style={{
          ...accountSkeletonBaseStyle,
          height: 300,
          borderRadius: 0,
        }}
      />
      <Container>
        <Row>
          <Col lg={12}>
            <div className="pt-3">
              <div className="mt-n5 d-flex gap-3 flex-wrap align-items-end">
                <div style={{ ...accountSkeletonBaseStyle, width: 96, height: 96, borderRadius: 24 }} />
                <div>
                  <div style={{ ...accountSkeletonBaseStyle, width: 220, height: 20, marginBottom: 12 }} />
                  <div style={{ ...accountSkeletonBaseStyle, width: 180, height: 14, marginBottom: 12 }} />
                  <div className="d-flex gap-2">
                    <div style={{ ...accountSkeletonBaseStyle, width: 110, height: 28, borderRadius: 999 }} />
                    <div style={{ ...accountSkeletonBaseStyle, width: 120, height: 28, borderRadius: 999 }} />
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>

    <section className="py-5">
      <Container>
        <Row>
          <Col lg={3}>
            <Card>
              <Card.Body>
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={`account-nav-skeleton-${index}`}
                    style={{
                      ...accountSkeletonBaseStyle,
                      height: 42,
                      marginBottom: index === 4 ? 0 : 14,
                    }}
                  />
                ))}
              </Card.Body>
            </Card>
          </Col>
          <Col lg={9}>
            <Card>
              <Card.Body>
                <Row className="g-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Col lg={6} key={`account-field-skeleton-${index}`}>
                      <div style={{ ...accountSkeletonBaseStyle, height: 16, width: "35%", marginBottom: 10 }} />
                      <div style={{ ...accountSkeletonBaseStyle, height: 46 }} />
                    </Col>
                  ))}
                  <Col lg={12}>
                    <div style={{ ...accountSkeletonBaseStyle, height: 16, width: "28%", marginBottom: 12, marginTop: 8 }} />
                  </Col>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Col lg={4} key={`account-password-skeleton-${index}`}>
                      <div style={{ ...accountSkeletonBaseStyle, height: 16, width: "45%", marginBottom: 10 }} />
                      <div style={{ ...accountSkeletonBaseStyle, height: 46 }} />
                    </Col>
                  ))}
                  <Col lg={12}>
                    <div style={{ ...accountSkeletonBaseStyle, width: 160, height: 42, marginTop: 10 }} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  </>
);

const MyAccount = () => {
  document.title = "My Account | Manser Commerce";

  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: me,
    isLoading: meLoading,
    isFetching: meFetching,
    isSuccess: isMeSuccess,
    refetch: refetchMe,
  } = useGetStoreMeQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const isCheckingAuth = meLoading || meFetching;
  const isLoggedIn = isMeSuccess && !!me?.id;

  const {
    data: profile,
    isLoading: profileLoading,
    isFetching: profileFetching,
    refetch: refetchProfile,
  } = useGetStoreMyProfileQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: addresses = [],
    isLoading: addressesLoading,
    isFetching: addressesFetching,
  } = useGetStoreMyAddressesQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: wishlistData,
    isLoading: wishlistLoading,
    isFetching: wishlistFetching,
    refetch: refetchWishlist,
  } = useGetStoreWishlistQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: ordersData,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
  } = useGetStoreOrdersQuery(
    { skip: 0, limit: 20 },
    {
      skip: !isLoggedIn,
      refetchOnMountOrArgChange: true,
    }
  );

  const [updateProfile, { isLoading: updatingProfile }] = useUpdateStoreMyProfileMutation();
  const [setPassword, { isLoading: settingPassword }] = useSetStorePasswordMutation();
  const [changePassword, { isLoading: changingPassword }] = useChangeStorePasswordMutation();
  const [confirmMobileVerifyWithFirebase, { isLoading: confirmingMobileVerify }] =
    useConfirmStoreMobileVerifyWithFirebaseMutation();
  const [requestEmailVerify, { isLoading: requestingEmailVerify }] =
    useRequestStoreEmailVerifyMutation();
  const [confirmEmailVerify, { isLoading: confirmingEmailVerify }] =
    useConfirmStoreEmailVerifyMutation();
  const [removeWishlistItem, { isLoading: removingWishlistItem }] =
    useRemoveStoreWishlistItemMutation();
  const [storeLogout, { isLoading: loggingOut }] = useStoreLogoutMutation();

  const [profileMessage, setProfileMessage] = useState<string>("");
  const [profileError, setProfileError] = useState<string>("");
  const [passwordMessage, setPasswordMessage] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [verificationMessage, setVerificationMessage] = useState<string>("");
  const [verificationError, setVerificationError] = useState<string>("");
  const [requestingMobileVerify, setRequestingMobileVerify] = useState(false);
  const [activeTab, setActiveTab] = useState<AccountTabKey>("profile");
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  const [settingsForm, setSettingsForm] = useState<SettingsFormState>({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [mobileVerifyForm, setMobileVerifyForm] = useState<VerifyFormState>({
    editing: false,
    otpRequested: false,
    otpCode: "",
    pendingValue: "",
    devOtp: "",
  });
  const [emailVerifyForm, setEmailVerifyForm] = useState<VerifyFormState>({
    editing: false,
    otpRequested: false,
    otpCode: "",
    pendingValue: "",
    devOtp: "",
  });

  const homeAddress = useMemo(() => {
    return (
      addresses.find((a) => a.is_default_billing) ||
      addresses.find((a) => a.address_type === "billing") ||
      addresses[0] ||
      null
    );
  }, [addresses]);

  const shippingAddress = useMemo(() => {
    return (
      addresses.find((a) => a.is_default_shipping) ||
      addresses.find((a) => a.address_type === "shipping") ||
      addresses[0] ||
      null
    );
  }, [addresses]);

  const displayName = useMemo(() => {
    const full =
      profile?.full_name?.trim() ||
      [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() ||
      me?.email?.split("@")[0]?.trim() ||
      me?.mobile_e164?.trim() ||
      "Customer";
    return full;
  }, [profile, me]);

  const initials = useMemo(() => {
    const source = displayName.trim();
    if (!source) return "CU";
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }, [displayName]);

  const locationText = useMemo(() => {
    const source = shippingAddress || homeAddress;
    if (!source) return "No location added";
    return [source.city, source.country_code].filter(Boolean).join(", ") || "No location added";
  }, [shippingAddress, homeAddress]);

  useEffect(() => {
    setSettingsForm({
      first_name: profile?.first_name ?? "",
      last_name: profile?.last_name ?? "",
      phone: me?.mobile_e164 ?? "",
      email: me?.email ?? "",
    });
  }, [profile, me, shippingAddress, homeAddress]);

  useEffect(() => {
    setMobileVerifyForm((prev) => ({
      ...prev,
      pendingValue: prev.otpRequested ? prev.pendingValue : me?.mobile_e164 ?? "",
    }));
    setEmailVerifyForm((prev) => ({
      ...prev,
      pendingValue: prev.otpRequested ? prev.pendingValue : me?.email ?? "",
    }));
  }, [me?.mobile_e164, me?.email]);

  const isInitialLoading = meLoading || profileLoading || addressesLoading;
  const isBusyTop = meFetching || profileFetching || addressesFetching;

  const formatMoney = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return "0";
    return `${value}`;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  const getAddressText = (address?: StoreAddress | null) => {
    if (!address) return "No address added";
    return [address.line1, address.line2, address.landmark, address.city, address.state, address.postal_code]
      .filter(Boolean)
      .join(", ");
  };

  const getErrorMessage = (err: any, fallback: string) => {
    const detail = err?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const messages = detail
        .map((item: any) => {
          if (typeof item?.msg === "string" && item.msg.trim()) {
            const field =
              Array.isArray(item?.loc) && item.loc.length > 1
                ? String(item.loc[item.loc.length - 1]).replace(/_/g, " ")
                : "";
            return field ? `${field}: ${item.msg}` : item.msg;
          }
          return null;
        })
        .filter(Boolean);

      if (messages.length > 0) {
        return messages.join(" ");
      }
    }
    if (typeof detail?.message === "string" && detail.message.trim()) return detail.message;
    return fallback;
  };

  const resetRecaptcha = () => {
    try {
      recaptchaRef.current?.clear();
    } catch {
    }

    recaptchaRef.current = null;
    window.myAccountRecaptchaVerifier = undefined;

    const container = document.getElementById(ACCOUNT_RECAPTCHA_ID);
    if (container) {
      container.innerHTML = "";
    }
  };

  const getOrCreateRecaptcha = async () => {
    const container = document.getElementById(ACCOUNT_RECAPTCHA_ID);
    if (!container) {
      throw new Error("reCAPTCHA container not found");
    }

    if (recaptchaRef.current) {
      return recaptchaRef.current;
    }

    if (window.myAccountRecaptchaVerifier) {
      recaptchaRef.current = window.myAccountRecaptchaVerifier;
      return window.myAccountRecaptchaVerifier;
    }

    const verifier = new RecaptchaVerifier(auth, ACCOUNT_RECAPTCHA_ID, {
      size: "invisible",
      callback: () => {},
      "expired-callback": () => {
        resetRecaptcha();
        setVerificationError("reCAPTCHA expired. Please try again.");
      },
    });

    await verifier.render();
    recaptchaRef.current = verifier;
    window.myAccountRecaptchaVerifier = verifier;
    return verifier;
  };

  const handleLogout = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    try {
      await storeLogout().unwrap();
    } catch {
    } finally {
      navigate("/auth-signin-basic", { replace: true });
    }
  };

  const handleProfileField = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setSettingsForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleVerifyField = (
    kind: "mobile" | "email",
    key: keyof VerifyFormState,
    value: string | boolean
  ) => {
    if (kind === "mobile") {
      setMobileVerifyForm((prev) => ({ ...prev, [key]: value }));
      return;
    }
    setEmailVerifyForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleUpdateProfile = async (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    e.preventDefault();
    setProfileMessage("");
    setProfileError("");
    setVerificationMessage("");
    setVerificationError("");

    try {
      await updateProfile({
        first_name: settingsForm.first_name || null,
        last_name: settingsForm.last_name || null,
        full_name:
          `${settingsForm.first_name || ""} ${settingsForm.last_name || ""}`.trim() || null,
      }).unwrap();

      setProfileMessage("Profile updated successfully");
      refetchProfile();
    } catch (err: any) {
      setProfileError(getErrorMessage(err, "Failed to update profile"));
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPasswordMessage("");
    setPasswordError("");

    try {
      if (!me?.has_password) {
        const res = await setPassword({
          new_password: passwordForm.new_password,
          confirm_password: passwordForm.confirm_password,
        }).unwrap();
        setPasswordMessage(res.message || "Password created successfully");
      } else {
        const res = await changePassword({
          old_password: passwordForm.old_password,
          new_password: passwordForm.new_password,
          confirm_password: passwordForm.confirm_password,
        }).unwrap();
        setPasswordMessage(res.message || "Password changed successfully");
      }

      setPasswordForm({
        old_password: "",
        new_password: "",
        confirm_password: "",
      });
      refetchMe();
    } catch (err: any) {
      setPasswordError(getErrorMessage(err, "Failed to update password"));
    }
  };

  const resetMobileVerificationState = (nextValue?: string) => {
    confirmationResultRef.current = null;
    resetRecaptcha();
    setMobileVerifyForm({
      editing: false,
      otpRequested: false,
      otpCode: "",
      pendingValue: nextValue ?? me?.mobile_e164 ?? "",
      devOtp: "",
    });
  };

  const resetEmailVerificationState = (nextValue?: string) => {
    setEmailVerifyForm({
      editing: false,
      otpRequested: false,
      otpCode: "",
      pendingValue: nextValue ?? me?.email ?? "",
      devOtp: "",
    });
  };

  const handleRequestMobileOtp = async () => {
    setActiveTab("setting");
    const mobile = settingsForm.phone.trim();
    setVerificationMessage("");
    setVerificationError("");
    if (!mobile) {
      setVerificationError("Phone number is required");
      return;
    }

    try {
      setRequestingMobileVerify(true);
      resetRecaptcha();
      const verifier = await getOrCreateRecaptcha();
      const confirmationResult = await signInWithPhoneNumber(auth, mobile, verifier);
      confirmationResultRef.current = confirmationResult;
      setVerificationMessage("OTP sent to mobile");
      setMobileVerifyForm((prev) => ({
        ...prev,
        editing: true,
        otpRequested: true,
        pendingValue: mobile,
        devOtp: "",
        otpCode: "",
      }));
    } catch (err: any) {
      resetRecaptcha();
      confirmationResultRef.current = null;
      const message =
        err?.code === "auth/invalid-app-credential"
          ? "Phone authentication failed. Check Firebase Phone sign-in, authorized domain, and reCAPTCHA setup."
          : err?.code === "auth/too-many-requests"
          ? "Too many attempts. Please wait a little and try again."
          : err?.message || "Failed to send mobile OTP";
      setVerificationError(message);
    } finally {
      setRequestingMobileVerify(false);
    }
  };

  const handleConfirmMobileOtp = async () => {
    setActiveTab("setting");
    const mobile = (mobileVerifyForm.pendingValue || settingsForm.phone).trim();
    const otpCode = mobileVerifyForm.otpCode.trim();
    setVerificationMessage("");
    setVerificationError("");
    if (!mobile || !otpCode) {
      setVerificationError("Phone number and OTP are required");
      return;
    }

    try {
      if (!confirmationResultRef.current) {
        throw new Error("OTP session not found. Please resend OTP.");
      }

      const credential = await confirmationResultRef.current.confirm(otpCode);
      const idToken = await credential.user.getIdToken(true);

      const res = await confirmMobileVerifyWithFirebase({
        id_token: idToken,
        mobile_e164: mobile,
      }).unwrap();
      setVerificationMessage(res.message || "Mobile verified successfully");
      await refetchMe();
      await signOut(auth).catch(() => undefined);
      resetMobileVerificationState(mobile);
    } catch (err: any) {
      setVerificationError(getErrorMessage(err, err?.message || "Failed to verify mobile"));
    }
  };

  const handleRequestEmailOtp = async () => {
    setActiveTab("setting");
    const email = settingsForm.email.trim();
    setVerificationMessage("");
    setVerificationError("");
    if (!email) {
      setVerificationError("Email is required");
      return;
    }

    try {
      const res = await requestEmailVerify({ email }).unwrap();
      setVerificationMessage(res.message || "OTP sent to email");
      setEmailVerifyForm((prev) => ({
        ...prev,
        editing: true,
        otpRequested: true,
        pendingValue: email,
        devOtp: res.otp_code || "",
        otpCode: "",
      }));
    } catch (err: any) {
      setVerificationError(getErrorMessage(err, "Failed to send email OTP"));
    }
  };

  const handleConfirmEmailOtp = async () => {
    setActiveTab("setting");
    const email = (emailVerifyForm.pendingValue || settingsForm.email).trim();
    const otpCode = emailVerifyForm.otpCode.trim();
    setVerificationMessage("");
    setVerificationError("");
    if (!email || !otpCode) {
      setVerificationError("Email and OTP are required");
      return;
    }

    try {
      const res = await confirmEmailVerify({ email, otp_code: otpCode }).unwrap();
      setVerificationMessage(res.message || "Email verified successfully");
      await refetchMe();
      resetEmailVerificationState(email);
    } catch (err: any) {
      setVerificationError(getErrorMessage(err, "Failed to verify email"));
    }
  };

  const handleRemoveWishlist = async (itemId: number) => {
    try {
      await removeWishlistItem(itemId).unwrap();
      refetchWishlist();
    } catch {
    }
  };

  useEffect(() => {
    return () => {
      confirmationResultRef.current = null;
      resetRecaptcha();
    };
  }, []);

  if (isCheckingAuth || isInitialLoading) {
    return <MyAccountSkeleton />;
  }

  if (!isLoggedIn) {
    return (
      <Navigate
        to="/auth-signin-basic"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <section className="position-relative">
        <div
          className="profile-basic position-relative"
          style={{
            backgroundImage: `url(${profilebg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            height: "300px",
          }}
        >
          <div className="bg-overlay bg-primary"></div>
        </div>
        <Container>
          <Row>
            <Col lg={12}>
              <div className="pt-3">
                <div className="mt-n5 d-flex gap-3 flex-wrap align-items-end">
                  {profile?.avatar_url ? (
                    <Image
                      src={profile.avatar_url}
                      alt={displayName}
                      className="avatar-xl p-1 bg-light mt-n3"
                      rounded
                    />
                  ) : (
                    <div
                      className="avatar-xl p-1 bg-light mt-n3 rounded d-flex align-items-center justify-content-center fw-semibold fs-2 text-dark"
                      style={{
                        width: 96,
                        height: 96,
                        border: "1px solid rgba(0,0,0,0.08)",
                        overflow: "hidden",
                      }}
                      title={displayName}
                    >
                      {initials}
                    </div>
                  )}

                  <div>
                    <h5 className="fs-18 mb-1">{displayName}</h5>
                    <div className="text-muted">
                      <i className="bi bi-geo-alt"></i> {locationText}
                    </div>
                    <div className="mt-2 d-flex gap-2 flex-wrap">
                      {me?.is_email_verified ? (
                        <Badge bg="success-subtle" text="success">
                          Email Verified
                        </Badge>
                      ) : null}
                      {me?.is_mobile_verified ? (
                        <Badge bg="success-subtle" text="success">
                          Mobile Verified
                        </Badge>
                      ) : null}
                    </div>
                  </div>

                  <div className="ms-md-auto d-flex align-items-center gap-2">
                    {isBusyTop ? <Spinner animation="border" size="sm" /> : null}
                      <Link to="/" className="btn btn-success btn-hover">
                        <i className="bi bi-cart4 me-1 align-middle"></i> Shopping Now
                      </Link>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="py-5">
        <Container>
          <Tab.Container
            id="left-tabs-example"
            activeKey={activeTab}
            onSelect={(eventKey) => {
              if (eventKey) {
                setActiveTab(eventKey as AccountTabKey);
              }
            }}
          >
            <Row>
              <Col lg={3}>
                <Card>
                  <Card.Body>
                    <Nav variant="pills" className="flex-column gap-3">
                      <Nav.Item as="li">
                        <Nav.Link as="a" eventKey="profile" className="fs-15" role="presentation">
                          <i className="bi bi-person-circle align-middle me-1"></i> Account Info
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item as="li">
                        <Nav.Link as="a" eventKey="list" className="fs-15" role="presentation">
                          <i className="bi bi-bookmark-check align-middle me-1"></i> Wish list
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item as="li">
                        <Nav.Link as="a" eventKey="order" className="fs-15" role="presentation">
                          <i className="bi bi-bag align-middle me-1"></i> Order
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item as="li">
                        <Nav.Link as="a" eventKey="setting" className="fs-15" role="presentation">
                          <i className="bi bi-gear align-middle me-1"></i> Settings
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item as="li">
                        <Nav.Link
                          as="a"
                          className="fs-15"
                          href="/auth-logout-basic"
                          onClick={handleLogout}
                        >
                          <i className="bi bi-box-arrow-right align-middle me-1"></i>{" "}
                          {loggingOut ? "Logging out..." : "Logout"}
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Card.Body>
                </Card>
              </Col>

              <Col sm={9}>
                <Tab.Content>
                  <Tab.Pane eventKey="profile">
                    <div
                      className="tab-pane fade show active"
                      id="custom-v-pills-profile"
                      role="tabpanel"
                    >
                      <Row>
                        <Col lg={12}>
                          <Card>
                            <Card.Body>
                              <div className="d-flex mb-4">
                                <h6 className="fs-16 text-decoration-underline flex-grow-1 mb-0">
                                  Personal Info
                                </h6>
                                <div className="flex-shrink-0">
                                  <Link to="#" className="badge bg-dark-subtle text-body">
                                    Edit
                                  </Link>
                                </div>
                              </div>

                              <div className="table-responsive table-card px-1">
                                <Table className="table-borderless table-sm">
                                  <tbody>
                                    <tr>
                                      <td>Customer Name</td>
                                      <td className="fw-medium">{displayName}</td>
                                    </tr>
                                    <tr>
                                      <td>Mobile / Phone Number</td>
                                      <td className="fw-medium">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                          <span>{me?.mobile_e164 || "-"}</span>
                                          {me?.is_mobile_verified ? (
                                            <Badge bg="success-subtle" text="success">
                                              Verified
                                            </Badge>
                                          ) : null}
                                        </div>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Email Address</td>
                                      <td className="fw-medium">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                          <span>{me?.email || "-"}</span>
                                          {me?.is_email_verified ? (
                                            <Badge bg="success-subtle" text="success">
                                              Verified
                                            </Badge>
                                          ) : null}
                                        </div>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Location</td>
                                      <td className="fw-medium">{locationText}</td>
                                    </tr>
                                    <tr>
                                      <td>Since Member</td>
                                      <td className="fw-medium">-</td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </div>

                              <div className="mt-4">
                                <h6 className="fs-16 text-decoration-underline">
                                  Billing &amp; Shipping Address
                                </h6>
                              </div>

                              <Row className="mt-4">
                                <Col md={6}>
                                  <Card className="mb-md-0">
                                    <Card.Body>
                                      <div className="float-end clearfix">
                                        <Link
                                          to="/shop/address"
                                          className="badge bg-primary-subtle text-primary"
                                        >
                                          <i className="ri-pencil-fill align-bottom me-1"></i> Edit
                                        </Link>
                                      </div>
                                      <div>
                                        <p className="mb-3 fw-semibold fs-12 d-block text-muted text-uppercase">
                                          Home Address
                                        </p>
                                        <h6 className="fs-14 mb-2 d-block">
                                          {homeAddress?.contact_name || displayName}
                                        </h6>
                                        <span className="text-muted fw-normal text-wrap mb-1 d-block">
                                          {getAddressText(homeAddress)}
                                        </span>
                                        <span className="text-muted fw-normal d-block">
                                          Mo. {homeAddress?.mobile_e164 || me?.mobile_e164 || "-"}
                                        </span>
                                      </div>
                                    </Card.Body>
                                  </Card>
                                </Col>

                                <Col md={6}>
                                  <Card className="mb-0">
                                    <Card.Body>
                                      <div className="float-end clearfix">
                                        <Link
                                          to="/shop/address"
                                          className="badge bg-primary-subtle text-primary"
                                        >
                                          <i className="ri-pencil-fill align-bottom me-1"></i> Edit
                                        </Link>
                                      </div>
                                      <div>
                                        <p className="mb-3 fw-semibold fs-12 d-block text-muted text-uppercase">
                                          Shipping Address
                                        </p>
                                        <h6 className="fs-14 mb-2 d-block">
                                          {shippingAddress?.contact_name || displayName}
                                        </h6>
                                        <span className="text-muted fw-normal text-wrap mb-1 d-block">
                                          {getAddressText(shippingAddress)}
                                        </span>
                                        <span className="text-muted fw-normal d-block">
                                          Mo.{" "}
                                          {shippingAddress?.mobile_e164 || me?.mobile_e164 || "-"}
                                        </span>
                                      </div>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey="list">
                    <div className="tab-pane fade show" id="custom-v-pills-list" role="tabpanel">
                      <Row>
                        <Col lg={12}>
                          <Card className="overflow-hidden">
                            <Card.Body>
                              <div className="table-responsive table-card">
                                <Table className="fs-15 align-middle">
                                  <thead>
                                    <tr>
                                      <th scope="col">Product</th>
                                      <th scope="col">Price</th>
                                      <th scope="col">Stock Status</th>
                                      <th scope="col">Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {wishlistLoading || wishlistFetching ? (
                                      <tr>
                                        <td colSpan={4} className="text-center py-4">
                                          <Spinner animation="border" size="sm" />
                                        </td>
                                      </tr>
                                    ) : (wishlistData?.items || []).length > 0 ? (
                                      (wishlistData?.items || []).map((item) => (
                                        <tr key={item.item_id}>
                                          <td>
                                            <div className="d-flex gap-3">
                                              <div className="avatar-sm flex-shrink-0">
                                                <div className="avatar-title bg-info-subtle rounded overflow-hidden">
                                                  {item.primary_image_url ? (
                                                    <Image
                                                      src={item.primary_image_url}
                                                      alt={item.product_name}
                                                      className="avatar-xs"
                                                    />
                                                  ) : (
                                                    <span className="fw-semibold text-info">
                                                      {item.product_name?.slice(0, 1)?.toUpperCase() ||
                                                        "P"}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="flex-grow-1">
                                                <Link to={`/product/${item.product_slug}`}>
                                                  <h6 className="fs-16 mb-1">
                                                    {item.product_name}
                                                  </h6>
                                                </Link>
                                                <p className="mb-0 text-muted fs-13">
                                                  {item.variant_name || item.product_slug}
                                                </p>
                                              </div>
                                            </div>
                                          </td>
                                          <td>₹{formatMoney(item.price)}</td>
                                          <td>
                                            <span className="badge bg-success-subtle text-success">
                                              In Stock
                                            </span>
                                          </td>
                                          <td>
                                            <ul className="list-unstyled d-flex gap-3 mb-0">
                                              <li>
                                                <Link
                                                  to="/shop/shopingcard"
                                                  className="btn btn-soft-info btn-icon btn-sm"
                                                >
                                                  <i className="ri-shopping-cart-2-line fs-13"></i>
                                                </Link>
                                              </li>
                                              <li>
                                                <Link
                                                  to="#"
                                                  className="btn btn-soft-danger btn-icon btn-sm"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    handleRemoveWishlist(item.item_id);
                                                  }}
                                                >
                                                  {removingWishlistItem ? (
                                                    <Spinner animation="border" size="sm" />
                                                  ) : (
                                                    <i className="ri-close-line fs-13"></i>
                                                  )}
                                                </Link>
                                              </li>
                                            </ul>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={4} className="text-center text-muted py-4">
                                          No wishlist items found
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              <div className="hstack gap-2 justify-content-end mt-4">
                                <Link to="/" className="btn btn-hover btn-secondary">
                                  Continue Shopping{" "}
                                  <i className="ri-arrow-right-line align-bottom"></i>
                                </Link>
                                <Link to="/shop/checkout" className="btn btn-hover btn-primary">
                                  Check Out <i className="ri-arrow-right-line align-bottom"></i>
                                </Link>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey="order">
                    <div className="tab-pane fade show" id="custom-v-pills-order" role="tabpanel">
                      <Card>
                        <Card.Body>
                          <div className="table-responsive table-card">
                            <Table className="fs-15 align-middle table-nowrap">
                              <thead>
                                <tr>
                                  <th scope="col">Order ID</th>
                                  <th scope="col">Product</th>
                                  <th scope="col">Date</th>
                                  <th scope="col">Total Amount</th>
                                  <th scope="col">Status</th>
                                  <th scope="col"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {ordersLoading || ordersFetching ? (
                                  <tr>
                                    <td colSpan={6} className="text-center py-4">
                                      <Spinner animation="border" size="sm" />
                                    </td>
                                  </tr>
                                ) : (ordersData?.items || []).length > 0 ? (
                                  (ordersData?.items || []).map((item) => {
                                    const firstItem = item.items?.[0];
                                    return (
                                      <tr key={item.id}>
                                        <td>
                                          <Link to="#" className="text-body">
                                            {item.order_no}
                                          </Link>
                                        </td>
                                        <td>
                                          <Link
                                            to={
                                              firstItem?.product_id
                                                ? `/product/${firstItem.product_id}`
                                                : "/product-list"
                                            }
                                          >
                                            <h6 className="fs-15 mb-1">
                                              {firstItem?.product_name_snapshot || "Order Item"}
                                            </h6>
                                          </Link>
                                          <p className="mb-0 text-muted fs-13">
                                            {[
                                              firstItem?.variant_name_snapshot || "",
                                              getCustomizationSummary(firstItem),
                                            ]
                                              .filter(Boolean)
                                              .join(" | ") || `${item.items?.length || 0} item(s)`}
                                          </p>
                                        </td>
                                        <td>
                                          <span className="text-muted">
                                            {formatDate(item.placed_at)}
                                          </span>
                                        </td>
                                        <td className="fw-medium">
                                          ₹{formatMoney(item.grand_total)}
                                        </td>
                                        <td>
                                          <span className="badge bg-info-subtle text-info">
                                            {item.status}
                                          </span>
                                        </td>
                                        <td>
                                          {canShowInvoice((item as any)?.payment_status) ? (
                                            <Link
                                              to={`/invoice/${item.id}`}
                                              data-bs-toggle="modal"
                                              className="btn btn-secondary btn-sm"
                                            >
                                              Invoice
                                            </Link>
                                          ) : (
                                            <span className="text-muted fs-12">Invoice after payment</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan={6} className="text-center text-muted py-4">
                                      No orders found
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>
                          <div className="text-end mt-4">
                            <Link to="/" className="btn btn-hover btn-primary">
                              Continue Shopping{" "}
                              <i className="ri-arrow-right-line align-middle ms-1"></i>
                            </Link>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey="setting">
                    <div className="tab-pane fade show" id="custom-v-pills-setting" role="tabpanel">
                      <Row>
                        <Col lg={12}>
                          <Card>
                            <Card.Body>
                              {profileMessage ? (
                                <Alert variant="success">{profileMessage}</Alert>
                              ) : null}
                              {profileError ? <Alert variant="danger">{profileError}</Alert> : null}
                              {passwordMessage ? (
                                <Alert variant="success">{passwordMessage}</Alert>
                              ) : null}
                              {passwordError ? (
                                <Alert variant="danger">{passwordError}</Alert>
                              ) : null}
                              {verificationMessage ? (
                                <Alert variant="success">{verificationMessage}</Alert>
                              ) : null}
                              {verificationError ? (
                                <Alert variant="danger">{verificationError}</Alert>
                              ) : null}
                              <div id={ACCOUNT_RECAPTCHA_ID} />

                              <Form>
                                <Row>
                                  <Col lg={12}>
                                    <h5 className="fs-16 text-decoration-underline mb-4">
                                      Personal Details
                                    </h5>
                                  </Col>

                                  <Col lg={6}>
                                    <div className="mb-3">
                                      <Form.Label htmlFor="firstnameInput">First Name</Form.Label>
                                      <Form.Control
                                        type="text"
                                        id="firstnameInput"
                                        placeholder="Enter your firstname"
                                        value={settingsForm.first_name}
                                        name="first_name"
                                        onChange={handleProfileField}
                                      />
                                    </div>
                                  </Col>

                                  <Col lg={6}>
                                    <div className="mb-3">
                                      <Form.Label htmlFor="lastnameInput">Last Name</Form.Label>
                                      <Form.Control
                                        type="text"
                                        id="lastnameInput"
                                        placeholder="Enter your lastname"
                                        value={settingsForm.last_name}
                                        name="last_name"
                                        onChange={handleProfileField}
                                      />
                                    </div>
                                  </Col>

                                  <Col lg={6}>
                                    <div className="mb-3">
                                      <Form.Label htmlFor="phonenumberInput">
                                        Phone Number
                                      </Form.Label>
                                      <Form.Control
                                        type="text"
                                        id="phonenumberInput"
                                        placeholder="Enter your phone number"
                                        value={settingsForm.phone}
                                        name="phone"
                                        onChange={handleProfileField}
                                        disabled={!mobileVerifyForm.editing}
                                      />
                                      <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                                        {me?.is_mobile_verified ? (
                                          <Badge bg="success-subtle" text="success">
                                            Verified
                                          </Badge>
                                        ) : (
                                          <Badge bg="warning-subtle" text="warning">
                                            Not Verified
                                          </Badge>
                                        )}
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant={mobileVerifyForm.editing ? "outline-secondary" : "outline-primary"}
                                          onClick={() => {
                                            setVerificationMessage("");
                                            setVerificationError("");
                                            if (mobileVerifyForm.editing) {
                                              setSettingsForm((prev) => ({
                                                ...prev,
                                                phone: me?.mobile_e164 ?? "",
                                              }));
                                              resetMobileVerificationState();
                                            } else {
                                              setMobileVerifyForm((prev) => ({
                                                ...prev,
                                                editing: true,
                                                pendingValue: settingsForm.phone,
                                              }));
                                            }
                                          }}
                                        >
                                          {mobileVerifyForm.editing ? "Cancel" : me?.mobile_e164 ? "Change" : "Add"}
                                        </Button>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="primary"
                                          disabled={
                                            !mobileVerifyForm.editing ||
                                            requestingMobileVerify ||
                                            !settingsForm.phone.trim()
                                          }
                                          onClick={handleRequestMobileOtp}
                                        >
                                          {requestingMobileVerify ? "Sending..." : "Send OTP"}
                                        </Button>
                                      </div>
                                      {mobileVerifyForm.otpRequested ? (
                                        <div className="mt-3">
                                          <Form.Control
                                            type="text"
                                            placeholder="Enter phone OTP"
                                            value={mobileVerifyForm.otpCode}
                                            onChange={(e) =>
                                              handleVerifyField("mobile", "otpCode", e.target.value)
                                            }
                                          />
                                          <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="success"
                                              disabled={confirmingMobileVerify || !mobileVerifyForm.otpCode.trim()}
                                              onClick={handleConfirmMobileOtp}
                                            >
                                              {confirmingMobileVerify ? "Verifying..." : "Verify Phone"}
                                            </Button>
                                            {mobileVerifyForm.devOtp ? (
                                              <small className="text-muted">
                                                Dev OTP: <strong>{mobileVerifyForm.devOtp}</strong>
                                              </small>
                                            ) : null}
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </Col>

                                  <Col lg={6}>
                                    <div className="mb-3">
                                      <Form.Label htmlFor="emailInput">Email Address</Form.Label>
                                      <Form.Control
                                        type="email"
                                        id="emailInput"
                                        placeholder="Enter your email"
                                        value={settingsForm.email}
                                        name="email"
                                        onChange={handleProfileField}
                                        disabled={!emailVerifyForm.editing}
                                      />
                                      <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                                        {me?.is_email_verified ? (
                                          <Badge bg="success-subtle" text="success">
                                            Verified
                                          </Badge>
                                        ) : (
                                          <Badge bg="warning-subtle" text="warning">
                                            Not Verified
                                          </Badge>
                                        )}
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant={emailVerifyForm.editing ? "outline-secondary" : "outline-primary"}
                                          onClick={() => {
                                            setVerificationMessage("");
                                            setVerificationError("");
                                            if (emailVerifyForm.editing) {
                                              setSettingsForm((prev) => ({
                                                ...prev,
                                                email: me?.email ?? "",
                                              }));
                                              resetEmailVerificationState();
                                            } else {
                                              setEmailVerifyForm((prev) => ({
                                                ...prev,
                                                editing: true,
                                                pendingValue: settingsForm.email,
                                              }));
                                            }
                                          }}
                                        >
                                          {emailVerifyForm.editing ? "Cancel" : me?.email ? "Change" : "Add"}
                                        </Button>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="primary"
                                          disabled={
                                            !emailVerifyForm.editing ||
                                            requestingEmailVerify ||
                                            !settingsForm.email.trim()
                                          }
                                          onClick={handleRequestEmailOtp}
                                        >
                                          {requestingEmailVerify ? "Sending..." : "Send OTP"}
                                        </Button>
                                      </div>
                                      {emailVerifyForm.otpRequested ? (
                                        <div className="mt-3">
                                          <Form.Control
                                            type="text"
                                            placeholder="Enter email OTP"
                                            value={emailVerifyForm.otpCode}
                                            onChange={(e) =>
                                              handleVerifyField("email", "otpCode", e.target.value)
                                            }
                                          />
                                          <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="success"
                                              disabled={confirmingEmailVerify || !emailVerifyForm.otpCode.trim()}
                                              onClick={handleConfirmEmailOtp}
                                            >
                                              {confirmingEmailVerify ? "Verifying..." : "Verify Email"}
                                            </Button>
                                            {emailVerifyForm.devOtp ? (
                                              <small className="text-muted">
                                                Dev OTP: <strong>{emailVerifyForm.devOtp}</strong>
                                              </small>
                                            ) : null}
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </Col>

                                </Row>
                              </Form>

                              <div className="mb-3" id="changePassword">
                                <h5 className="fs-16 text-decoration-underline mb-4">
                                  {me?.has_password ? "Change Password" : "Create Password"}
                                </h5>

                                <Form onSubmit={handlePasswordSubmit}>
                                  <Row className="g-2">
                                    {me?.has_password ? (
                                      <Col lg={4}>
                                        <div>
                                          <Form.Label htmlFor="oldpasswordInput">
                                            Old Password*
                                          </Form.Label>
                                          <Form.Control
                                            type="password"
                                            id="oldpasswordInput"
                                            placeholder="Enter current password"
                                            name="old_password"
                                            autoComplete="off"
                                            value={passwordForm.old_password}
                                            onChange={handlePasswordField}
                                          />
                                        </div>
                                      </Col>
                                    ) : null}

                                    <Col lg={me?.has_password ? 4 : 6}>
                                      <div>
                                        <Form.Label htmlFor="newpasswordInput">
                                          New Password*
                                        </Form.Label>
                                        <Form.Control
                                          type="password"
                                          id="newpasswordInput"
                                          placeholder="Enter new password"
                                          name="new_password"
                                          autoComplete="off"
                                          value={passwordForm.new_password}
                                          onChange={handlePasswordField}
                                        />
                                      </div>
                                    </Col>

                                    <Col lg={me?.has_password ? 4 : 6}>
                                      <div>
                                        <Form.Label htmlFor="confirmpasswordInput">
                                          Confirm Password*
                                        </Form.Label>
                                        <Form.Control
                                          type="password"
                                          id="confirmpasswordInput"
                                          placeholder="Confirm password"
                                          name="confirm_password"
                                          autoComplete="off"
                                          value={passwordForm.confirm_password}
                                          onChange={handlePasswordField}
                                        />
                                      </div>
                                    </Col>

                                    <Col lg={12}>
                                      <div className="mb-3">
                                        <Link
                                          to="/auth-pass-reset-basic"
                                          className="link-primary text-decoration-underline"
                                        >
                                          Forgot Password ?
                                        </Link>
                                      </div>
                                    </Col>

                                    <Col lg={12}>
                                      <Button
                                        type="submit"
                                        variant="secondary"
                                        disabled={settingPassword || changingPassword}
                                      >
                                        {settingPassword || changingPassword ? (
                                          <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Please wait...
                                          </>
                                        ) : (
                                          <>
                                            <i className="ri-lock-password-line align-middle me-2"></i>
                                            {me?.has_password ? "Update Password" : "Create Password"}
                                          </>
                                        )}
                                      </Button>
                                    </Col>
                                  </Row>
                                </Form>
                              </div>

                              <div className="text-sm-end">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  className="d-block d-sm-inline-block"
                                  onClick={handleUpdateProfile}
                                >
                                  {updatingProfile ? (
                                    <>
                                      <Spinner animation="border" size="sm" className="me-2" />
                                      Updating...
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-edit-box-line align-middle me-2"></i> Update
                                      Profile
                                    </>
                                  )}
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Container>
      </section>

    </React.Fragment>
  );
};

export default MyAccount;
